import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { addFlight, addIssue, getFlights, getTachInfo, getUsers, uploadFlightFiles } from '../../api';
import { uploadIssueFiles, TACH_FILE_ACCEPT, TACH_FILE_HINT } from '../../utils/issueFiles';
import IssueFilePicker from '../../components/IssueFilePicker/IssueFilePicker';
import AddRefuelModal from '../../components/AddRefuelModal/AddRefuelModal';
import { Flight } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useAircraft } from '../../aircraft/AircraftContext';
import { useFeedback } from '../../hooks/useFeedback';
import { getApiErrorMessage } from '../../utils/apiError';
import { flightPilotOptions, isFlyableMember, isGeneralUser } from '../../utils/isGeneralUser';
import { formatHours } from '../../utils/flightDisplay';
import {
  DEFAULT_FLIGHT_TYPE,
  ENGINE_RUN_TYPE,
  FLIGHT_TYPES,
  formatRefuelLine,
  parseFlightNumber,
  parseLandings,
  refuelsPayload,
  type FlightRefuelDraft,
} from '../../utils/flightForm';
import {
  fuelSplitError,
  PENDING_HOUR_FLIGHT_ID,
  refuelPurchaseTotal,
} from '../../utils/expenseSplit';
import PageBackLink from '../../components/PageBackLink/PageBackLink';
import './FlightsPage.css';

const getToday = () => {
  return new Date().toISOString().split('T')[0];
};

const linkedHoursTach = (
  field: 'manual_hours' | 'hobbs_end',
  raw: string,
  tachTip: number,
  hasBaseline: boolean
) => {
  if (!raw.trim()) {
    return { manual_hours: '', hobbs_end: '' };
  }
  const parsed = parseFlightNumber(raw);
  if (parsed === null) {
    return { [field]: raw } as {
      manual_hours?: string;
      hobbs_end?: string;
    };
  }
  if (field === 'manual_hours') {
    return {
      manual_hours: raw,
      hobbs_end: formatHours(hasBaseline ? tachTip + parsed : parsed),
    };
  }
  const hours = hasBaseline ? parsed - tachTip : parsed;
  return {
    hobbs_end: raw,
    manual_hours: hours >= 0 ? formatHours(hours) : '',
  };
};

const emptyFlightForm = (pilotId: number) => ({
  pilot_id: pilotId,
  flight_date: getToday(),
  hobbs_end: '',
  manual_hours: '',
  flight_type: DEFAULT_FLIGHT_TYPE,
  landings: '',
  oil: '',
  issue_description: '',
  issue_severity: '',
  comment: '',
  showComment: false,
});

const FlightsPage = () => {
  const [flights, setFlights] = useState<Flight[]>([]);
  const [tachTip, setTachTip] = useState(0);
  const [form, setForm] = useState(emptyFlightForm(0));
  const [refuels, setRefuels] = useState<FlightRefuelDraft[]>([]);
  const [refuelEditor, setRefuelEditor] = useState<{
    index: number | null;
  } | null>(null);
  const [showOil, setShowOil] = useState(false);
  const [issueFiles, setIssueFiles] = useState<File[]>([]);
  const [tachFiles, setTachFiles] = useState<File[]>([]);
  const [users, setUsers] = useState<
    { id: number; name: string; login?: string; role?: string }[]
  >([]);
  const { showError, showSuccess, feedbackNode } = useFeedback();
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const { isAdmin, currentUserId } = useAuth();
  const { aircraftId, current, dataRevision } = useAircraft();

  const defaultFuelLocation = current?.base_airfield?.trim() || '';

  const aircraftHint = current
    ? `${current.tail_number || ''} ${current.model || ''}`.trim()
    : aircraftId
      ? `Борт #${aircraftId}`
      : '';

  const pilotOptions = useMemo(
    () =>
      flightPilotOptions(users, {
        isAdmin,
        currentUserId,
      }),
    [users, isAdmin, currentUserId]
  );

  const defaultPilotId = useMemo(
    () =>
      pilotOptions.find((u) => u.id === currentUserId)?.id ??
      pilotOptions[0]?.id ??
      0,
    [pilotOptions, currentUserId]
  );

  const effectivePilotId = form.pilot_id || defaultPilotId;
  const isEngineRun = form.flight_type === ENGINE_RUN_TYPE;
  const payerOptions = useMemo(
    () => users.filter((user) => !isGeneralUser(user)).map((user) => ({
      id: user.id,
      name: user.name,
    })),
    [users]
  );

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await getUsers();
        setUsers(res.data);
      } catch (error) {
        showError(getApiErrorMessage(error, 'Ошибка загрузки пользователей'));
      }
    };
    fetchUsers();
  }, [showError]);

  useEffect(() => {
    if (!defaultPilotId) return;
    setForm((prev) => {
      const stillValid = pilotOptions.some((u) => u.id === prev.pilot_id);
      if (prev.pilot_id === 0 || !stillValid) {
        return { ...prev, pilot_id: defaultPilotId };
      }
      return prev;
    });
  }, [defaultPilotId, pilotOptions]);

  useEffect(() => {
    const controller = new AbortController();
    const run = async () => {
      let list: Flight[] = [];
      try {
        const flightsRes = await getFlights({ signal: controller.signal });
        list = flightsRes.data;
        setFlights(list);
      } catch (error) {
        if (controller.signal.aborted) return;
        showError(getApiErrorMessage(error, 'Ошибка загрузки полётов'));
        return;
      }
      try {
        const tachRes = await getTachInfo();
        if (!controller.signal.aborted) setTachTip(tachRes.data.tach_current ?? 0);
      } catch (error) {
        if (controller.signal.aborted) return;
        const tip = list[0]?.tach_end ?? list[0]?.hobbs_end ?? 0;
        setTachTip(tip);
        showError(getApiErrorMessage(error, 'Не удалось загрузить показание тахометра'));
      }
    };
    void run();
    return () => controller.abort();
  }, [aircraftId, dataRevision, showError]);

  const loadFlights = async () => {
    let list: Flight[] = [];
    try {
      const flightsRes = await getFlights();
      list = flightsRes.data;
      setFlights(list);
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка загрузки полётов'));
      return;
    }
    try {
      const tachRes = await getTachInfo();
      setTachTip(tachRes.data.tach_current ?? 0);
    } catch (error) {
      const tip = list[0]?.tach_end ?? list[0]?.hobbs_end ?? 0;
      setTachTip(tip);
      showError(getApiErrorMessage(error, 'Не удалось загрузить показание тахометра'));
    }
  };

  const handleTypeChange = (flight_type: string) => {
    setForm((prev) => ({
      ...prev,
      flight_type,
      landings:
        flight_type === ENGINE_RUN_TYPE
          ? '0'
          : prev.flight_type === ENGINE_RUN_TYPE && prev.landings === '0'
            ? ''
            : prev.landings,
    }));
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      if (!form.flight_date) {
        showError('Выберите дату!');
        return;
      }

      if (form.flight_date > getToday()) {
        showError('Дата полёта не может быть позже сегодняшнего дня!');
        return;
      }

      if (form.issue_description.trim() && !form.issue_severity) {
        showError('Укажите степень важности неисправности');
        return;
      }

      // Актуальный tip: иначе после удаления/чужого полёта «часы ≠ дельта таха».
      let tip = tachTip;
      let list = flights;
      try {
        const [flightsRes, tachRes] = await Promise.all([
          getFlights(),
          getTachInfo(),
        ]);
        list = flightsRes.data;
        setFlights(list);
        tip = tachRes.data.tach_current ?? 0;
        setTachTip(tip);
      } catch (error) {
        tip = list[0]?.tach_end ?? list[0]?.hobbs_end ?? tip;
      }

      let hours = 0;
      let hobbsEnd = 0;
      const hasBaseline = list.length > 0 || tip > 0;

      if (form.manual_hours.trim()) {
        const parsedHours = parseFlightNumber(form.manual_hours);
        if (parsedHours === null || !Number.isFinite(parsedHours)) {
          showError('Введите число в моточасах');
          return;
        }
        hours = parsedHours;
        if (hours < 0) {
          showError('Моточасы не могут быть отрицательными!');
          return;
        }
        hobbsEnd = hasBaseline
          ? Math.round((tip + hours) * 10) / 10
          : hours;
      } else {
        if (!form.hobbs_end.trim()) {
          showError('Введите тахометр или моточасы!');
          return;
        }
        const parsedHobbs = parseFlightNumber(form.hobbs_end);
        if (parsedHobbs === null || !Number.isFinite(parsedHobbs)) {
          showError('Введите число в показании тахометра');
          return;
        }
        hobbsEnd = parsedHobbs;

        if (hasBaseline) {
          hours = Math.round((hobbsEnd - tip) * 10) / 10;

          if (hours < 0) {
            showError('Тахометр не может быть меньше текущего!');
            return;
          }
        } else {
          hours = hobbsEnd;
        }
      }

      for (const item of refuels) {
        const splitError = fuelSplitError(
          {
            costSplit: item.costSplit,
            manualShares: item.manualShares,
            flightIds: item.flightIds,
            payerUserId: item.payerUserId || effectivePilotId,
          },
          refuelPurchaseTotal([item]) > 0
        );
        if (splitError) {
          showError(splitError);
          return;
        }
      }

      try {
        const flightRes = await addFlight({
          pilot_id: effectivePilotId,
          flight_date: form.flight_date,
          hobbs_end: hobbsEnd,
          hours: hours,
          flight_type: form.flight_type || DEFAULT_FLIGHT_TYPE,
          landings: parseLandings(form.landings, form.flight_type),
          oil: showOil && form.oil !== '' ? parseFloat(form.oil) : null,
          comment: form.comment.trim() || '',
          refuels: refuelsPayload(refuels),
        });

        let filesFailed = false;
        if (tachFiles.length > 0 && flightRes.data.id) {
          try {
            await uploadFlightFiles(flightRes.data.id, tachFiles);
          } catch (error) {
            filesFailed = true;
            showError(
              getApiErrorMessage(
                error,
                'Полёт добавлен, но фото тахометра загрузить не удалось'
              )
            );
          }
        }

        const hasIssue = Boolean(form.issue_description.trim());
        if (hasIssue) {
          const selectedPilot = users.find((u) => u.id === effectivePilotId);
          const issueAuthorId =
            selectedPilot && isGeneralUser(selectedPilot)
              ? currentUserId
              : effectivePilotId;
          const created = await addIssue({
            user_id: issueAuthorId,
            description: form.issue_description,
            severity: form.issue_severity || 'medium',
            flight_id: flightRes.data.id,
            issue_date: form.flight_date,
          });
          if (issueFiles.length > 0 && created.data.id) {
            try {
              await uploadIssueFiles(created.data.id, issueFiles);
            } catch (error) {
              filesFailed = true;
              showError(
                getApiErrorMessage(
                  error,
                  'Полёт добавлен, неисправность зафиксирована, но файлы загрузить не удалось'
                )
              );
            }
          }
        }

        const soloDefault =
          users.find((u) => u.id === currentUserId)?.id ??
          users.find((u) => isFlyableMember(u) && !isGeneralUser(u))?.id ??
          0;
        setForm(emptyFlightForm(soloDefault));
        setRefuels([]);
        setShowOil(false);
        setIssueFiles([]);
        setTachFiles([]);

        if (!filesFailed) {
          showSuccess(
            hasIssue
              ? 'Полёт добавлен, неисправность зафиксирована'
              : 'Полёт успешно добавлен'
          );
        }

        await loadFlights();
      } catch (error) {
        showError(getApiErrorMessage(error, 'Ошибка добавления полёта'));
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div className="flights-page">
      {feedbackNode}
      <div className="flights-header">
        <PageBackLink to="/" />
        <h2>Добавление полёта</h2>
        <Link to="/stats#history" className="flights-history-link">
          История полётов
        </Link>
      </div>

      <form className="add-form" onSubmit={handleAdd}>
        <h3 className="form-section-title">Информация о полёте</h3>
        {(aircraftHint || tachTip > 0) && (
          <p className="form-hint">
            {aircraftHint}
            {aircraftHint && tachTip > 0 ? ' · ' : ''}
            {tachTip > 0 ? `тах ${tachTip}` : ''}
          </p>
        )}
        <select
          value={form.pilot_id}
          onChange={(e) => setForm({ ...form, pilot_id: Number(e.target.value) })}
          aria-label="Пилот"
        >
          {pilotOptions.map((user) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={form.flight_date}
          onChange={(e) => setForm({ ...form, flight_date: e.target.value })}
          max={getToday()}
        />
        <input
          type="text"
          inputMode="decimal"
          autoComplete="off"
          placeholder="Моточасы"
          value={form.manual_hours}
          onKeyDown={(e) => {
            if (e.key === '-' || e.key === 'Subtract') {
              e.preventDefault();
            }
          }}
          onChange={(e) => {
            const hasBaseline = flights.length > 0 || tachTip > 0;
            const value = e.target.value.replace(/[-−–—]/g, '');
            setForm({
              ...form,
              ...linkedHoursTach(
                'manual_hours',
                value,
                tachTip,
                hasBaseline
              ),
            });
          }}
        />
        <input
          type="text"
          inputMode="decimal"
          autoComplete="off"
          placeholder="Тахометр"
          value={form.hobbs_end}
          onChange={(e) => {
            const hasBaseline = flights.length > 0 || tachTip > 0;
            setForm({
              ...form,
              ...linkedHoursTach(
                'hobbs_end',
                e.target.value,
                tachTip,
                hasBaseline
              ),
            });
          }}
        />
        <div className="form-span issue-files-field">
          <IssueFilePicker
            files={tachFiles}
            onChange={setTachFiles}
            label="Фото тахометра"
            accept={TACH_FILE_ACCEPT}
            hint={TACH_FILE_HINT}
          />
        </div>

        <select
          value={form.flight_type}
          onChange={(e) => handleTypeChange(e.target.value)}
          aria-label="Тип полёта"
        >
          {FLIGHT_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>

        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="Посадки"
          value={isEngineRun ? '0' : form.landings}
          onChange={(e) => setForm({ ...form, landings: e.target.value })}
          disabled={isEngineRun}
        />

        <h3 className="form-section-title">Заправка</h3>
        {refuels.map((item, index) => (
          <div key={item.key} className="fuel-row">
            <button
              type="button"
              className="fuel-row-main"
              onClick={() => setRefuelEditor({ index })}
            >
              {formatRefuelLine(item)}
            </button>
            <button
              type="button"
              className="fuel-row-remove"
              onClick={() =>
                setRefuels((current) =>
                  current.filter((_, itemIndex) => itemIndex !== index)
                )
              }
            >
              Убрать
            </button>
          </div>
        ))}
        <button
          type="button"
          className="form-add-link"
          onClick={() => setRefuelEditor({ index: null })}
        >
          {refuels.length > 0
            ? '+ добавить ещё заправку топливом'
            : '+ добавить заправку топливом'}
        </button>
        {refuels.length > 0 ? (
          <p className="form-hint">
            Распределение можно поменять, открыв строку заправки.
          </p>
        ) : null}
        {showOil ? (
          <div className="fuel-row">
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              className="field-oil-input"
              placeholder="Доливка масла, л"
              value={form.oil}
              onChange={(e) => setForm({ ...form, oil: e.target.value })}
            />
            <button
              type="button"
              className="fuel-row-remove"
              onClick={() => {
                setShowOil(false);
                setForm((prev) => ({ ...prev, oil: '' }));
              }}
            >
              Убрать
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="form-add-link"
            onClick={() => setShowOil(true)}
          >
            + добавить доливку масла в двигатель
          </button>
        )}

        <h3 className="form-section-title">Добавить неисправность</h3>
        <input
          type="text"
          placeholder="Неисправность"
          value={form.issue_description}
          onChange={(e) =>
            setForm({ ...form, issue_description: e.target.value })
          }
        />
        <select
          value={form.issue_severity}
          onChange={(e) =>
            setForm({ ...form, issue_severity: e.target.value })
          }
        >
          <option value="" disabled>
            Степень важности
          </option>
          <option value="low">Низкая</option>
          <option value="medium">Средняя</option>
          <option value="high">Высокая</option>
        </select>
        <div className="issue-files-field">
          <IssueFilePicker files={issueFiles} onChange={setIssueFiles} />
        </div>

        {form.showComment || form.comment ? (
          <div className="form-span">
            <label className="form-inline-label">Комментарий / заметка</label>
            <textarea
              className="form-note"
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
              placeholder="Любая информация по этому полёту..."
              rows={4}
            />
          </div>
        ) : (
          <button
            type="button"
            className="form-add-link"
            onClick={() => setForm({ ...form, showComment: true })}
          >
            + Добавить комментарий/заметку
          </button>
        )}

        <button type="submit" className="btn-add" disabled={submitting}>
          {submitting ? 'Добавление…' : 'Добавить'}
        </button>
      </form>

      {refuelEditor ? (
        <AddRefuelModal
          defaultLocation={defaultFuelLocation}
          initial={
            refuelEditor.index != null ? refuels[refuelEditor.index] : null
          }
          date={form.flight_date}
          defaultPayerId={effectivePilotId}
          payerOptions={payerOptions}
          allowFullSplit={isAdmin}
          lockedHourFlight={{
            id: PENDING_HOUR_FLIGHT_ID,
            hours: parseFlightNumber(form.manual_hours) || 0,
            pilot_id: effectivePilotId,
            pilot:
              users.find((user) => user.id === effectivePilotId)?.name ||
              'Этот полёт',
            label: 'Этот полёт',
          }}
          onClose={() => setRefuelEditor(null)}
          onSave={(item) => {
            setRefuels((current) => {
              if (refuelEditor.index == null) {
                return [
                  ...current,
                  { ...item, key: `${Date.now()}-${current.length}` },
                ];
              }
              return current.map((row, index) =>
                index === refuelEditor.index ? { ...row, ...item } : row
              );
            });
            setRefuelEditor(null);
          }}
        />
      ) : null}
    </div>
  );
};

export default FlightsPage;
